// src/hooks/usePaymentAccounts.js
import { useState, useEffect, useCallback } from "react";
import { paymentAccountsService } from "../services/paymentAccountsService";
import { toast } from "react-hot-toast";

/**
 * usePaymentAccounts Hook
 * Handles fetching, creating, updating, and deleting payment accounts
 */
export const usePaymentAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  /** 🔁 Mapper: Frontend snake_case → Backend camelCase */
  const mapToApiFormat = (data = {}) => ({
    accountName: data.account_name || "",
    accountType: data.account_type || "",
    upiId: data.upi_id || "",
    paymentGateway: data.payment_gateway || "",
    bankName: data.bank_name || "",
    accountNumber: data.account_number || "",
    accountHolderName: data.account_holder_name || "",
    terminalId: data.terminal_id || "",
    merchantId: data.merchant_id || "",
    machineProvider: data.machine_provider || "",
    counterLocation: data.counter_location || "",
    description: data.description || "",
    isActive: data.is_active ?? true,
    isDefault: data.is_default ?? false,
    displayOrder: Number(data.display_order) || 0,
    todaysCollection: data.todays_collection ?? data.todaysCollection ?? 0,

  });

  /** 🔁 Mapper: Backend camelCase → Frontend snake_case */
  const mapFromApiFormat = (data = {}) => ({
    id: data.id,
    account_name: data.accountName ?? data.account_name ?? "",
    account_type: data.accountType ?? data.account_type ?? "",
    upi_id: data.upiId ?? data.upi_id ?? "",
    payment_gateway: data.paymentGateway ?? data.payment_gateway ?? "",
    bank_name: data.bankName ?? data.bank_name ?? "",
    account_number: data.accountNumber ?? data.account_number ?? "",
    account_holder_name:
      data.accountHolderName ?? data.account_holder_name ?? "",
    terminal_id: data.terminalId ?? data.terminal_id ?? "",
    merchant_id: data.merchantId ?? data.merchant_id ?? "",
    machine_provider: data.machineProvider ?? data.machine_provider ?? "",
    counter_location: data.counterLocation ?? data.counter_location ?? "",
    description: data.description ?? "",
    is_active: data.isActive ?? data.is_active ?? true,
    is_default: data.isDefault ?? data.is_default ?? false,
    display_order: data.displayOrder ?? data.display_order ?? 0,
    todays_collection: data.todaysCollection ?? 0,
    created_at: data.createdAt ?? data.created_at,
    updated_at: data.updatedAt ?? data.updated_at,
    created_by: data.createdBy ?? data.created_by,
    updated_by: data.updatedBy ?? data.updated_by,
    branchId: data.branchId ?? null,
    branchName: data.branchName ?? null,
  });

  /** ✅ Fetch all payment accounts */
  const fetchAccounts = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const response = await paymentAccountsService.getAll(filters);

      // Handle flexible API response structures
      const data =
        Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      if (!Array.isArray(data)) {
        console.warn("⚠️ Unexpected response format:", response);
        setAccounts([]);
        return;
      }

      const mappedData = data.map(mapFromApiFormat);
      setAccounts(mappedData);
    } catch (error) {
      console.error("❌ Failed to fetch payment accounts:", error);
      toast.error("Failed to fetch payment accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /** ✅ Create new payment account */
  const createAccount = async (data) => {
    try {
      const payload = mapToApiFormat(data);
      await paymentAccountsService.create(payload);
      toast.success("Payment account created successfully");
      await fetchAccounts();
    } catch (error) {
      console.error("❌ Failed to create payment account:", error);
      toast.error("Failed to create payment account");
      throw error;
    }
  };

  /** ✅ Update existing payment account */
  const updateAccount = async (id, data) => {
    try {
      const payload = mapToApiFormat(data);
      await paymentAccountsService.update(id, payload);
      toast.success("Payment account updated successfully");
      await fetchAccounts();
    } catch (error) {
      console.error(`❌ Failed to update payment account ${id}:`, error);
      toast.error("Failed to update payment account");
      throw error;
    }
  };

  /** ✅ Delete payment account */
  const deleteAccount = async (id) => {
    try {
      await paymentAccountsService.delete(id);
      toast.success("Payment account deleted");
      setAccounts((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      console.error(`❌ Failed to delete payment account ${id}:`, error);
      toast.error("Failed to delete payment account");
      throw error;
    }
  };

  /** 🔁 Auto-fetch on mount */
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  
const refreshData = () => {
    fetchAccounts();
  };
  
  return {
    accounts,
    loading,
    fetchAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    refreshData,
  };
};

export default usePaymentAccounts;
